import { FC, useEffect, useCallback, useContext, useMemo } from 'react';
import { Box, Select } from '@robocorp/components';
import { CompletedS3Source } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { useTranslation } from 'react-i18next';
import { VariablesContext } from '../context';
import { useActions, useSelector } from '../../../../hooks';

export const SelectRobotId: FC = () => {
  const { endpoint, variables, setVariables } = useContext(VariablesContext);
  const { t } = useTranslation();

  const loadingRobots = useSelector(({ entityStates }) => entityStates.activityTemplates.loading);
  const activityTemplateById = useSelector(
    ({ workspaceResources }) => workspaceResources.activityTemplates.byId,
  );

  const { getActivityTemplates } = useActions(({ activityTemplateActions }) => ({
    getActivityTemplates: activityTemplateActions.getActivityTemplates,
  }));

  const robotItems = useMemo(
    () =>
      Object.entries(activityTemplateById)
        /**
         * If api endpoint requires a Robot task, display only robots with tasks defined
         */
        .filter(([, robot]) => {
          if (!endpoint.includes('robotTask')) {
            return true;
          }

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const config = robot.config as CompletedS3Source;
          const { tasks } = config.package || {
            tasks: undefined,
          };

          return typeof tasks === 'object';
        })
        .map(([id, robot]) => {
          return {
            label: robot.name,
            value: id,
          };
        }),

    [activityTemplateById],
  );

  const robotTaskItems = useMemo(() => {
    const robot = Object.entries(activityTemplateById).find(([id]) => id === variables.robotId);
    if (robot) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const config = robot[1].config as CompletedS3Source;
      const { tasks } = config.package || {
        tasks: undefined,
      };
      return tasks ? Object.keys(tasks).map((item) => ({ label: item, value: item })) : [];
    }

    return [];
  }, [variables.robotId]);

  useEffect(() => {
    if (!variables.robotTask && robotTaskItems.length) {
      setVariables({ robotTask: robotTaskItems[0].value });
    }
  }, [robotTaskItems]);

  useEffect(() => {
    if (endpoint.includes('robotId')) {
      getActivityTemplates();
    }
  }, [endpoint]);

  const onChange = useCallback(
    (robotId: string) => {
      setVariables({ robotId, robotTask: undefined, robotRunId: undefined });
    },
    [setVariables],
  );

  const onChangeTask = useCallback(
    (robotTask: string) => {
      setVariables({ robotTask });
    },
    [setVariables],
  );

  let placeholder = robotItems.length ? `${t('choose')}...` : t('noActivityTemplates');
  if (loadingRobots) {
    placeholder = t('loading');
  }

  let placeholderTasks = robotTaskItems.length ? `${t('choose')}...` : t('noTasks');
  if (loadingRobots) {
    placeholderTasks = t('loading');
  }

  return (
    <>
      {!endpoint.includes('robotId') ? null : (
        <Box mb={20}>
          <Select
            label={t('chooseRobot')}
            items={robotItems}
            onChange={onChange}
            disabled={loadingRobots || robotItems.length < 1}
            value={variables.robotId}
            placeholder={placeholder}
          />
        </Box>
      )}
      {!endpoint.includes('robotTask') ? null : (
        <Box mb={20}>
          <Select
            label={t('chooseRobotTask')}
            items={robotTaskItems}
            onChange={onChangeTask}
            disabled={loadingRobots || robotTaskItems.length < 1}
            value={variables.robotTask}
            placeholder={placeholderTasks}
          />
        </Box>
      )}
    </>
  );
};
